import { Season, Venue2 } from '@seatix/shared-domain-event';

/**
 * The shape both display helpers actually read off a "date": enough to
 * format a day and, when available, resolve its timezone. Deliberately
 * narrower than `EventListWithVenueAndMedia` — the catalog's full events
 * satisfy it structurally, and so do the thin `dates[]` entries a season
 * purchase row carries (no `venue` of their own; the caller supplies one).
 */
export interface DisplayableDate {
  starts_at: string;
  venue?: Pick<Venue2, 'name' | 'timezone'>;
}

/**
 * A season borrows its venue, name and date badge from its first date — but
 * never its image: it owns its own `media[]`, read directly, and a season with
 * none renders "sin imagen" rather than borrowing a date's picture. seatix-api
 * refuses to borrow one server-side and has a test saying so, and "mis boletos"
 * receives no date media at all, so a client-side fallback would paint the same
 * pass two different ways depending on the screen.
 *
 * That is a choice of representative, not of value: seatix-api rejects a season
 * at creation unless its dates share venue, chart, service charge and PSP, so
 * any of them paints the same card. The API declines to derive this itself —
 * a season owns no venue or name of its own — which leaves the derivation to
 * the client. It happens here and nowhere else.
 *
 * Takes only the `name` so the same function serves the catalog's `Season` and
 * the narrower season an order carries, which has no `object_key`,
 * `total_games` or `expiration_at` to offer.
 */
export function seasonDisplayName(
  season: Pick<Season, 'name'>,
  firstDate?: DisplayableDate,
): string {
  return season.name ?? `Abono · ${firstDate?.venue?.name ?? 'Temporada'}`;
}

/**
 * Cuántas fechas cubre el abono, sin nombrar ninguna.
 *
 * La forma que usan las superficies donde el abono ya está identificado y lo que
 * falta decir es su tamaño — no cuál es su primera fecha, que es lo que responde
 * {@link seasonDateLabel}.
 *
 * Existía como la misma expresión escrita a mano en tres sitios
 * (`feat-season-details.page.ts`, `season-seat-selection.source.ts` y el
 * fallback de `seasonDateLabel` aquí abajo). Vive aquí para que la cuarta no se
 * escriba; los tres originales siguen en pie y son una limpieza aparte.
 */
export function seasonDateCountLabel(count: number): string {
  return count === 1 ? '1 fecha' : `${count} fechas`;
}

/**
 * A season has no `starts_at` of its own, and no single date is honest about a
 * product valid on several. The badge keeps the first date; this line says how
 * many more the pass covers.
 */
export function seasonDateLabel(
  dates: readonly DisplayableDate[],
  firstDate: DisplayableDate,
): string {
  const remaining = dates.length - 1;
  const suffix =
    remaining > 0
      ? ` y ${remaining} ${remaining === 1 ? 'fecha más' : 'fechas más'}`
      : '';

  // The card guards its own date the same way. Formatting an absent or
  // unparseable `starts_at` throws a RangeError, and this runs while building
  // the whole catalog — one bad date would blank the entire home page.
  const startsAt = Date.parse(firstDate.starts_at);
  if (Number.isNaN(startsAt)) {
    return dates.length > 1 ? seasonDateCountLabel(dates.length) : '';
  }

  const formatted = new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    timeZone: firstDate.venue?.timezone || 'UTC',
  }).format(startsAt);

  return `${formatted}${suffix}`;
}
